import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SquareArrowOutUpRight, Trash2 } from 'lucide-react';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AnimateSpin } from '@/components/AnimateSpin';
import { ProductImageFallback } from '@/components/ProductImageFallback';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useRemoveMerchandisingCollectionSliceProduct } from '@/hooks/useRemoveMerchandisingCollectionSliceProduct';
import { cn, priceFormatterV2 } from '@/lib/utils';
import { MerchandisingCollectionSliceProduct } from '@/services/MerchandisingCollectionsService';

interface ProductCardProps {
  product: MerchandisingCollectionSliceProduct;
  isSaving: boolean;
  onSaving: (value: boolean) => void;
}

export function ProductCard({ product, isSaving, onSaving }: ProductCardProps) {
  const [imageError, setImageError] = useState(false);

  const { collectionId, sliceId } = useParams();

  const { user } = useAuth();

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.SliceProducts.components.ProductCard',
  });

  const { removeSliceProduct } = useRemoveMerchandisingCollectionSliceProduct();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.productId });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  async function handleRemoveProduct() {
    if (isSaving) {
      return;
    }

    onSaving(true);

    try {
      await removeSliceProduct({
        collectionId: Number(collectionId),
        sliceId: Number(sliceId),
        productId: product.productId,
      });

      toast.success(t('toast.success'));
    } catch {
      toast.error(t('toast.error'));
    } finally {
      onSaving(false);
    }
  }

  return (
    <Card
      className={cn(
        'relative',
        isDragging && 'opacity-80 ring-2 ring-indigo-400',
        isSaving && 'pointer-events-none',
      )}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className="absolute right-4 top-4 flex items-center gap-1">
        <Button asChild size="sm">
          <Link
            to={ROUTE_PATHS.VISION_PRODUCT.replace(
              ':productId',
              String(product.id),
            )}
            target="_blank"
          >
            <SquareArrowOutUpRight className="h-3 w-3" />
          </Link>
        </Button>

        <Button
          variant="destructive"
          size="sm"
          disabled={isSaving}
          onClick={handleRemoveProduct}
        >
          {isSaving ? (
            <AnimateSpin className="h-3 w-3" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
        </Button>
      </div>

      <CardContent className="p-4">
        {!product.active && (
          <div className="z-5 absolute inset-0 flex items-center justify-center rounded-md bg-black/60">
            <span className="rounded-full bg-black/80 px-3 py-1 text-sm font-semibold text-white">
              {t('inactive')}
            </span>
          </div>
        )}

        <div className="flex flex-col space-y-3">
          {imageError ? (
            <ProductImageFallback
              showMessage={false}
              className="mx-auto h-60 w-full rounded-md object-cover lg:h-40 xl:h-60"
            />
          ) : (
            <img
              src={product.image1}
              alt={product.name}
              className="mx-auto h-60 w-full rounded-md object-cover lg:h-40 xl:h-60"
              onError={() => setImageError(true)}
            />
          )}

          <div className="space-y-2">
            <h4 className="line-clamp-2 text-sm font-medium">{product.name}</h4>
            <p className="text-xs text-muted-foreground">
              {product.visionCategoryName}
            </p>

            <div className="mt-1 flex items-center space-x-2">
              {parseFloat(product.price) > parseFloat(product.salePrice) && (
                <span className="text-sm line-through opacity-50">
                  {priceFormatterV2(
                    parseFloat(product.price),
                    user!.customer.language,
                    user!.customer.platform,
                  )}
                </span>
              )}

              <span
                className={cn(
                  'text-sm font-semibold',
                  parseFloat(product.price) > parseFloat(product.salePrice) &&
                    'text-red-400',
                )}
              >
                {priceFormatterV2(
                  parseFloat(product.salePrice),
                  user!.customer.language,
                  user!.customer.platform,
                )}
              </span>
            </div>

            <Badge
              variant={product.stock ? 'default' : 'secondary'}
              className="w-fit text-xs"
            >
              {product.stock ? t('inStock') : t('outOfStock')}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
